import { http, HttpResponse } from "msw";
import { NodeType, IkeSAState, ChildSAState, IpsecOffloadState } from "@libs/appliance_common";
import { NodeHealthStatus } from "../../pages/Cluster/types";
const locations = [
    "US-East-1",
    "US-West-2",
    "EU-Central-1",
    "AP-Southeast-1"
];
const cpuSpecs = [
    "Intel(R) Xeon(R) E-2388G",
    "Intel(R) Xeon(R) 6515P"
];
const memorySpecs = [
    "32 GB",
    "64 GB",
    "128 TB",
    "512 GB"
];
const storageSpecs = [
    "256GB NVMe SSD",
    "1TB NVMe SSD",
    "2TB NVMe SSD",
    "4TB NVMe SSD"
];
const generateNode = (index, status, nodeType, isControlPlane = false)=>{
    const locationIdx = index % locations.length;
    const cpuIdx = index % cpuSpecs.length;
    const memoryIdx = index % memorySpecs.length;
    const storageIdx = index % storageSpecs.length;
    return {
        nodeId: `node-${String(index + 1).padStart(4, "0")}`,
        description: `${nodeType} Node ${index + 1}`,
        location: locations[locationIdx],
        nodeType,
        controlPlane: isControlPlane,
        networkInterfaces: [
            {
                interface: "eth0",
                ip_address: `10.0.${Math.floor(index / 256)}.${index % 256}`,
                subnet_mask: "255.255.255.0"
            },
            {
                interface: "eth1",
                ip_address: `10.1.${Math.floor(index / 256)}.${index % 256}`,
                subnet_mask: "255.255.255.0"
            },
            {
                interface: "enp3s0f1",
                ip_address: `192.168.${Math.floor(index / 256)}.${index % 256}`,
                subnet_mask: "255.255.255.0"
            },
            {
                interface: "tmfifo_net0",
                ip_address: `192.168.${Math.floor(index / 256)}.${index % 256}`,
                subnet_mask: "255.255.255.0"
            }
        ],
        hardware: {
            manufacturer: "Supermicro",
            model: nodeType === NodeType.QEA ? `TB116-DL` : `SYS-522B-WR`,
            cpu: cpuSpecs[cpuIdx],
            memory: memorySpecs[memoryIdx],
            storage: storageSpecs[storageIdx],
            hasDPU: nodeType === NodeType.AIAppliance
        },
        healthStatus: status,
        lastCheck: new Date(Date.now() - Math.floor(Math.random() * 10 * 60 * 1000)),
        errors: []
    };
};
export const mockNodes = [
    // First node is the control plane
    generateNode(0, NodeHealthStatus.Healthy, NodeType.QEA, true),
    // Healthy QEA nodes
    ...Array.from({
        length: 7
    }, (_, i)=>generateNode(i + 1, NodeHealthStatus.Healthy, NodeType.QEA)),
    // Error QEA nodes (was degraded/critical)
    generateNode(8, NodeHealthStatus.Error, NodeType.QEA),
    generateNode(9, NodeHealthStatus.Error, NodeType.QEA),
    generateNode(10, NodeHealthStatus.Error, NodeType.QEA),
    // Offline QEA node
    generateNode(11, NodeHealthStatus.Offline, NodeType.QEA),
    // Healthy AI Appliance nodes
    ...Array.from({
        length: 3
    }, (_, i)=>generateNode(12 + i, NodeHealthStatus.Healthy, NodeType.AIAppliance)),
    // Error AI Appliance node
    generateNode(15, NodeHealthStatus.Error, NodeType.AIAppliance)
];
export const calculateStats = (nodes)=>{
    return {
        totalNodes: nodes.length,
        healthy: nodes.filter((n)=>n.healthStatus === NodeHealthStatus.Healthy).length,
        error: nodes.filter((n)=>n.healthStatus === NodeHealthStatus.Error).length,
        offline: nodes.filter((n)=>n.healthStatus === NodeHealthStatus.Offline).length
    };
};
/**
 * Generate per-node IPsec status mock data.
 * Models a cluster where:
 * - node-0002 (192.168.0.1) has tunnels to node-0003 (192.168.0.2) and node-0004 (192.168.0.3)
 * - node-0003 (192.168.0.2) reports the mirror of the tun-1 tunnel back to node-0002
 * - node-0004 (192.168.0.3) reports the mirror of the tun-2 tunnel back to node-0002
 */ function generateIpsecStatus(nodeIndex) {
    const getRandomBytes = ()=>Math.floor(Math.random() * 1_000_000_000_000);
    const getRandomPackets = ()=>Math.floor(Math.random() * 1_000_000_000);
    function makeEndpoint(ip, nodeType = NodeType.QEA) {
        return {
            id: ip,
            host: ip,
            port: 500,
            auth_class: "pre-shared key",
            total_conns: 1,
            localhost: false,
            node_type: nodeType
        };
    }
    function makeChildSA(uniqueid, name, localTs, remoteTs) {
        const bytes_in = getRandomBytes();
        const packets_in = getRandomPackets();
        const bytes_out = getRandomBytes();
        const packets_out = getRandomPackets();
        return {
            uniqueid,
            name,
            sa_key: `${name}-${uniqueid}`,
            state: ChildSAState.INSTALLED,
            mode: "TRANSPORT",
            protocol: "ESP",
            spi_in: "c9f71560",
            spi_out: "c07dc54f",
            encr_alg: "AES_GCM_16",
            encr_keysize: 256,
            ake1: "KYBER_L3",
            ake2: "BLAST",
            bytes_in,
            packets_in,
            bytes_out,
            packets_out,
            life_time: 3954,
            install_time: 6,
            rekey_time_config: 3600,
            rekey_time_state: 3462,
            rekey_packets_config: 0,
            negotiated_local_ts: localTs,
            negotiated_remote_ts: remoteTs,
            configured_local_ts: localTs,
            configured_remote_ts: remoteTs
        };
    }
    function makeConnection(uniqueid, name, localIp, remoteIp, offloadState = null) {
        const outChild = makeChildSA(uniqueid, `${name}-out`, [
            `${localIp}/32[udp]`
        ], [
            `${remoteIp}/32[udp/4789]`
        ]);
        const inChild = makeChildSA(String(Number(uniqueid) + 1), `${name}-in`, [
            `${localIp}/32[udp/4789]`
        ], [
            `${remoteIp}/32[udp]`
        ]);
        return {
            uniqueid,
            name,
            local_endpoint_id: localIp,
            remote_endpoint_id: remoteIp,
            state: IkeSAState.ESTABLISHED,
            ike_version: 2,
            established_time: 1761851746,
            encr_alg: "AES_CBC",
            integ_alg: "HMAC_SHA2_256_128",
            prf_alg: "PRF_HMAC_SHA2_256",
            dh_group: "CURVE_25519",
            config_version: "IKEv2",
            config_local_addrs: [
                localIp
            ],
            config_remote_addrs: [
                remoteIp
            ],
            total_bytes_in: outChild.bytes_in + inChild.bytes_in,
            total_bytes_out: outChild.bytes_out + inChild.bytes_out,
            total_packets_in: outChild.packets_in + inChild.packets_in,
            total_packets_out: outChild.packets_out + inChild.packets_out,
            rekey_time_config: 14400,
            rekey_time_state: 12000,
            children: {
                [`${name}-out`]: outChild,
                [`${name}-in`]: inChild
            },
            offload_state: offloadState
        };
    }
    // Node index 1 = node-0002, IP 192.168.0.1 — two tunnels
    if (nodeIndex === 1) {
        const conn1 = makeConnection("2", "tun-1", "192.168.0.1", "192.168.0.2");
        const conn2 = makeConnection("4", "tun-2", "192.168.0.1", "192.168.0.3");
        return {
            endpoints: {
                "192.168.0.1": makeEndpoint("192.168.0.1"),
                "192.168.0.2": makeEndpoint("192.168.0.2"),
                "192.168.0.3": makeEndpoint("192.168.0.3")
            },
            connections: {
                "tun-1": conn1,
                "tun-2": conn2
            }
        };
    }
    // Node index 2 = node-0003, IP 192.168.0.2 — mirror of tun-1
    if (nodeIndex === 2) {
        const conn = makeConnection("2", "tun-1", "192.168.0.2", "192.168.0.1");
        return {
            endpoints: {
                "192.168.0.2": makeEndpoint("192.168.0.2"),
                "192.168.0.1": makeEndpoint("192.168.0.1")
            },
            connections: {
                "tun-1": conn
            }
        };
    }
    // Node index 3 = node-0004, IP 192.168.0.3 — mirror of tun-2
    if (nodeIndex === 3) {
        const conn = makeConnection("4", "tun-2", "192.168.0.3", "192.168.0.1");
        return {
            endpoints: {
                "192.168.0.3": makeEndpoint("192.168.0.3"),
                "192.168.0.1": makeEndpoint("192.168.0.1")
            },
            connections: {
                "tun-2": conn
            }
        };
    }
    // Node index 12 = node-0013, AIAppliance, IP 192.168.0.12
    // DPU-to-DPU tunnel with FullPacketOffload to node-0014 (192.168.0.13)
    // DPU-to-DPU tunnel with NoOffload (error) to node-0015 (192.168.0.14)
    if (nodeIndex === 12) {
        const conn1 = makeConnection("10", "dpu-tun-1", "192.168.0.12", "192.168.0.13", IpsecOffloadState.FullPacketOffload);
        const conn2 = makeConnection("12", "dpu-tun-2", "192.168.0.12", "192.168.0.14", IpsecOffloadState.NoOffload);
        return {
            endpoints: {
                "192.168.0.12": makeEndpoint("192.168.0.12", NodeType.AIAppliance),
                "192.168.0.13": makeEndpoint("192.168.0.13", NodeType.AIAppliance),
                "192.168.0.14": makeEndpoint("192.168.0.14", NodeType.AIAppliance)
            },
            connections: {
                "dpu-tun-1": conn1,
                "dpu-tun-2": conn2
            }
        };
    }
    // Node index 13 = node-0014, AIAppliance, IP 192.168.0.13 — mirror of dpu-tun-1
    if (nodeIndex === 13) {
        const conn = makeConnection("10", "dpu-tun-1", "192.168.0.13", "192.168.0.12", IpsecOffloadState.FullPacketOffload);
        return {
            endpoints: {
                "192.168.0.13": makeEndpoint("192.168.0.13", NodeType.AIAppliance),
                "192.168.0.12": makeEndpoint("192.168.0.12", NodeType.AIAppliance)
            },
            connections: {
                "dpu-tun-1": conn
            }
        };
    }
    // Node index 14 = node-0015, AIAppliance, IP 192.168.0.14 — mirror of dpu-tun-2
    if (nodeIndex === 14) {
        const conn = makeConnection("12", "dpu-tun-2", "192.168.0.14", "192.168.0.12", IpsecOffloadState.NoOffload);
        return {
            endpoints: {
                "192.168.0.14": makeEndpoint("192.168.0.14", NodeType.AIAppliance),
                "192.168.0.12": makeEndpoint("192.168.0.12", NodeType.AIAppliance)
            },
            connections: {
                "dpu-tun-2": conn
            }
        };
    }
    // All other nodes: no IPsec tunnels
    return null;
}
// Convert Node to NodeQueryResult based on requested fields
function nodeToQueryResult(node, nodeIndex, fields) {
    // Initialize with null values as per the API contract
    const result = {
        node_id: null,
        description: null,
        location: null,
        control_plane: null,
        node_type: null,
        memory: null,
        cpu: null,
        has_dpu: null,
        updated_at: null,
        node_status_json: null,
        manufacturer: null,
        model: null,
        network_interfaces: null,
        errors: null,
        ipsec_status: null
    };
    if (fields.includes("node_id")) {
        result.node_id = node.nodeId; // Mock uses string, API uses UUID - both are string in JSON
    }
    if (fields.includes("description")) {
        result.description = node.description;
    }
    if (fields.includes("location")) {
        result.location = node.location;
    }
    if (fields.includes("control_plane")) {
        result.control_plane = node.controlPlane;
    }
    if (fields.includes("node_type")) {
        result.node_type = node.nodeType;
    }
    if (fields.includes("memory")) {
        result.memory = node.hardware.memory;
    }
    if (fields.includes("cpu")) {
        result.cpu = node.hardware.cpu;
    }
    if (fields.includes("has_dpu")) {
        result.has_dpu = node.hardware.hasDPU;
    }
    if (fields.includes("updated_at")) {
        // Generate dynamic timestamps based on intended health status
        // Offline nodes: more than 1 minute ago
        // Healthy/Error nodes: within the last 30 seconds
        if (node.healthStatus === NodeHealthStatus.Offline) {
            result.updated_at = new Date(Date.now() - 2 * 60 * 1000).toISOString(); // 2 minutes ago
        } else {
            result.updated_at = new Date(Date.now() - Math.floor(Math.random() * 30 * 1000)).toISOString(); // 0-30 seconds ago
        }
    }
    if (fields.includes("manufacturer")) {
        result.manufacturer = node.hardware.manufacturer;
    }
    if (fields.includes("model")) {
        result.model = node.hardware.model;
    }
    if (fields.includes("network_interfaces")) {
        // Return parsed arrays (backend now parses JSON strings into structured types)
        result.network_interfaces = node.networkInterfaces.map((iface, i)=>({
                interface: iface.interface,
                ip_address: iface.ip_address,
                subnet_mask: iface.subnet_mask,
                gateway: "10.0.0.1",
                mac_address: `00:11:22:33:44:${String(i).padStart(2, "0")}`,
                mtu: 1500,
                interface_speed_mbps: 1000,
                is_physical: true
            }));
    }
    if (fields.includes("errors")) {
        const now = Math.floor(Date.now() / 1000);
        result.errors = node.healthStatus === NodeHealthStatus.Error ? [
            {
                source: "Card",
                timestamp: now - 10,
                error_msg: "QRNG card not responding: hardware timeout after 5s"
            },
            {
                source: "System",
                timestamp: now - 45,
                error_msg: "Entropy pool critically low: 12% remaining"
            },
            {
                source: "Card",
                timestamp: now - 90,
                error_msg: "Card firmware version mismatch: expected 2.1.0, found 1.9.3"
            },
            {
                source: "Application",
                timestamp: now - 180,
                error_msg: "Could not reach configured BLAST URL"
            },
            {
                source: "Command",
                timestamp: now - 300,
                error_msg: "Key distribution command failed: peer unreachable",
                command_type: "DistributeKey",
                global_id: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                command_id: "1712345678-0"
            },
            {
                source: "System",
                timestamp: now - 420,
                error_msg: "Memory usage exceeded 95% threshold"
            },
            {
                source: "Card",
                timestamp: now - 600,
                error_msg: "QRNG entropy output below minimum bit rate (< 1 Mbps)"
            },
            {
                source: "Command",
                timestamp: now - 900,
                error_msg: "HSM seeding operation timed out after 30s",
                command_type: "SeedHSM",
                global_id: "b2c3d4e5-f6a7-8901-bcde-f12345678901",
                command_id: "1712345679-0"
            },
            {
                source: "Application",
                timestamp: now - 1200,
                error_msg: "TLS handshake failed with upstream entropy relay"
            },
            {
                source: "System",
                timestamp: now - 1500,
                error_msg: "NTP synchronization lost: clock drift exceeds 500ms"
            },
            {
                source: "Card",
                timestamp: now - 1800,
                error_msg: "Self-test failure on QRNG channel 2: bias detected"
            },
            {
                source: "Command",
                timestamp: now - 2100,
                error_msg: "Bulk entropy transfer aborted: checksum mismatch",
                command_type: "TransferEntropy",
                global_id: "c3d4e5f6-a7b8-9012-cdef-123456789012",
                command_id: "1712345680-0"
            },
            {
                source: "Application",
                timestamp: now - 2400,
                error_msg: "Redis connection pool exhausted: 0 available connections"
            },
            {
                source: "System",
                timestamp: now - 3000,
                error_msg: "Disk I/O latency spike: average write latency 850ms"
            },
            {
                source: "Card",
                timestamp: now - 3600,
                error_msg: "Temperature warning: QRNG module at 78°C (threshold: 75°C)"
            }
        ] : [];
    }
    if (fields.includes("ipsec_status")) {
        result.ipsec_status = generateIpsecStatus(nodeIndex);
    }
    return result;
}
const nodesHandlers = [
    http.get("*/api/cluster/control-plane/nodes", ({ request })=>{
        const url = new URL(request.url);
        const fieldsParam = url.searchParams.get("fields");
        const nodeIdFilter = url.searchParams.get("node_id");
        if (!fieldsParam) {
            return HttpResponse.json({
                message: "Fields parameter is required"
            }, {
                status: 422
            });
        }
        const fields = fieldsParam.split(",").map((f)=>f.trim());
        // Filter nodes if node_id is specified
        let filteredNodes = mockNodes;
        if (nodeIdFilter) {
            filteredNodes = mockNodes.filter((n)=>n.nodeId === nodeIdFilter);
        }
        const nodes = filteredNodes.map((node)=>{
            const nodeIndex = mockNodes.indexOf(node);
            return nodeToQueryResult(node, nodeIndex, fields);
        });
        const response = {
            nodes
        };
        return HttpResponse.json(response);
    }),
    // Handler for updating a node
    http.put("*/api/cluster/control-plane/nodes/:nodeId", async ({ params, request })=>{
        const { nodeId } = params;
        const body = await request.json();
        // Find the node
        const nodeIndex = mockNodes.findIndex((n)=>n.nodeId === nodeId);
        if (nodeIndex === -1) {
            return HttpResponse.json({
                message: "Node not found"
            }, {
                status: 404
            });
        }
        // Validate that at least one field is provided
        if (body.description === undefined && body.location === undefined) {
            return HttpResponse.json({
                message: "At least one of 'description' or 'location' must be provided"
            }, {
                status: 400
            });
        }
        // Update the node
        const node = mockNodes[nodeIndex];
        if (body.description !== undefined) {
            node.description = body.description;
        }
        if (body.location !== undefined) {
            node.location = body.location;
        }
        return HttpResponse.json({
            success: true
        });
    }),
    // Handler for deleting a node
    http.delete("*/api/cluster/control-plane/nodes/:nodeId", ({ params })=>{
        const { nodeId } = params;
        const nodeIndex = mockNodes.findIndex((n)=>n.nodeId === nodeId);
        if (nodeIndex === -1) {
            return HttpResponse.json({
                message: "Node not found"
            }, {
                status: 404
            });
        }
        const node = mockNodes[nodeIndex];
        if (node.controlPlane) {
            return HttpResponse.json({
                message: "Cannot delete a control plane node"
            }, {
                status: 422
            });
        }
        mockNodes.splice(nodeIndex, 1);
        return HttpResponse.json({
            success: true,
            message: "Node removed"
        });
    })
];
export default nodesHandlers;
